document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let revealObserver;
  let revealScrollBound = false;

  const reveal = (item) => {
    item.classList.add('is-visible');
    item.dataset.revealReady = 'true';
    revealObserver?.unobserve(item);
  };

  const revealPassedItems = () => {
    document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((item) => {
      if (item.getBoundingClientRect().top < window.innerHeight * 0.96) reveal(item);
    });
  };

  function initReveals(scope = document) {
    const items = [...scope.querySelectorAll('[data-reveal]:not([data-reveal-ready])')];
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach(reveal);
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) reveal(entry.target);
        });
      }, { rootMargin: '0px 0px -4% 0px', threshold: 0 });
    }
    items.forEach((item) => {
      item.dataset.revealReady = 'true';
      if (item.getBoundingClientRect().top < window.innerHeight * 0.96) reveal(item);
      else revealObserver.observe(item);
    });
    if (!revealScrollBound) {
      let queued = false;
      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          revealPassedItems();
          queued = false;
        });
      }, { passive: true });
      revealScrollBound = true;
    }
  }

  function initHeader(scope = document) {
    const header = scope.querySelector('[data-galix-header]');
    if (!header || header.dataset.galixReady === 'true') return;
    header.dataset.galixReady = 'true';
    const controller = new AbortController();
    header.galixController = controller;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 32);
    update();
    window.addEventListener('scroll', update, { passive: true, signal: controller.signal });
    const toggle = header.querySelector('[data-menu-toggle]');
    const menu = header.querySelector('[data-mobile-menu]');
    const backdrop = header.querySelector('[data-menu-backdrop]');
    if (toggle && menu) {
      const mobileHome = window.matchMedia('(max-width: 600px)');
      const setMenuState = (open, returnFocus = false) => {
        toggle.setAttribute('aria-expanded', String(open));
        menu.hidden = !open;
        if (backdrop) backdrop.hidden = !(open && document.body.matches('.template-index, .template-product') && mobileHome.matches);
        document.documentElement.classList.toggle('galix-menu-open', open);
        if (returnFocus) toggle.focus();
      };
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        setMenuState(open);
        if (open) menu.querySelector('a')?.focus();
      }, { signal: controller.signal });
      menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) setMenuState(false);
      }, { signal: controller.signal });
      document.addEventListener('click', (event) => {
        if (menu.hidden || menu.contains(event.target) || toggle.contains(event.target)) return;
        setMenuState(false, true);
      }, { signal: controller.signal });
      backdrop?.addEventListener('click', () => setMenuState(false, true), { signal: controller.signal });
      header.querySelector('[data-cart-open]')?.addEventListener('click', () => setMenuState(false), { signal: controller.signal });
      mobileHome.addEventListener('change', () => setMenuState(false), { signal: controller.signal });
      document.addEventListener('keydown', (event) => {
        if (menu.hidden) return;
        if (event.key === 'Escape') {
          setMenuState(false, true);
        } else if (event.key === 'Tab' && backdrop && !backdrop.hidden) {
          const focusable = [toggle, ...menu.querySelectorAll('a')];
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }, { signal: controller.signal });
      controller.signal.addEventListener('abort', () => {
        document.documentElement.classList.remove('galix-menu-open');
        if (backdrop) backdrop.hidden = true;
      }, { once: true });
    }
  }

  function initComparison(scope = document) {
    scope.querySelectorAll('[data-comparison]:not([data-galix-ready])').forEach((section) => {
      section.dataset.galixReady = 'true';
      const stage = section.querySelector('.galix-comparison__stage');
      const range = section.querySelector('[data-comparison-range]');
      if (!stage || !range) return;
      const controller = new AbortController();
      section.galixController = controller;
      let current = Number(range.value);
      let target = current;
      let frame = 0;
      const clamp = (value) => Math.max(0, Math.min(100, value));
      const render = () => {
        frame = 0;
        current = reducedMotion ? target : current + (target - current) * 0.3;
        if (Math.abs(target - current) < 0.08) current = target;
        stage.style.setProperty('--split', `${current.toFixed(2)}%`);
        if (current !== target) frame = window.requestAnimationFrame(render);
      };
      const moveTo = (value) => {
        target = clamp(value);
        if (reducedMotion && frame) window.cancelAnimationFrame(frame);
        if (!frame || reducedMotion) frame = window.requestAnimationFrame(render);
      };
      range.addEventListener('input', () => moveTo(Number(range.value)), { signal: controller.signal });
      controller.signal.addEventListener('abort', () => window.cancelAnimationFrame(frame), { once: true });
      stage.style.setProperty('--split', `${current}%`);
    });
  }

  function initBenefits(scope = document) {
    scope.querySelectorAll('[data-benefits]:not([data-benefits-cinematic]):not([data-galix-ready])').forEach((section) => {
      section.dataset.galixReady = 'true';
      const tabs = [...section.querySelectorAll('[data-benefit-tab]')];
      const scene = section.querySelector('.galix-benefits__scene');
      const background = section.querySelector('.galix-benefits__background');
      const visual = section.querySelector('[data-benefits-visual]');
      const beam = section.querySelector('[data-benefits-beam]');
      const lens = section.querySelector('[data-benefits-lens]');
      const aura = section.querySelector('.galix-benefits__aura');
      const controller = new AbortController();
      section.galixController = controller;
      let activeIndex = -1;
      let queued = false;

      const applyVisualMotion = (progress, desktop) => {
        const bounded = Math.min(1, Math.max(0, progress));
        const eased = bounded * bounded * (3 - 2 * bounded);
        const pulse = Math.sin(bounded * Math.PI);
        section.dataset.motionProgress = eased.toFixed(4);

        if (visual) {
          const x = desktop ? -24 * eased : -8 * eased;
          const y = desktop ? -8 * pulse : -5 * pulse;
          const rotation = desktop ? -0.72 * pulse : -0.32 * pulse;
          const scale = desktop ? 1.02 + 0.04 * eased : 1 + 0.018 * eased;
          visual.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0) rotate(${rotation.toFixed(3)}deg) scale(${scale.toFixed(4)})`;
        }
        if (beam) {
          const beamRotation = -1.8 + eased * 3.6;
          const beamScaleX = 0.92 + pulse * 0.18;
          const beamScaleY = 0.92 + pulse * 0.14;
          beam.style.opacity = (0.02 + pulse * 0.07).toFixed(3);
          beam.style.transform = `rotate(${beamRotation.toFixed(3)}deg) scale3d(${beamScaleX.toFixed(3)},${beamScaleY.toFixed(3)},1)`;
        }
        if (lens) {
          lens.style.opacity = (0.38 + pulse * 0.34).toFixed(3);
          lens.style.transform = `translate(-50%,-50%) scale(${(0.82 + pulse * 0.36).toFixed(3)})`;
        }
      };

      const activate = (tab, focus = false) => {
        const nextIndex = tabs.indexOf(tab);
        if (nextIndex < 0) return;
        tabs.forEach((item, index) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.classList.toggle('is-past', index < nextIndex);
          item.setAttribute('aria-expanded', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        activeIndex = nextIndex;
        section.dataset.scrollStep = String(nextIndex + 1);
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab), { signal: controller.signal });
        tab.addEventListener('keydown', (event) => {
          let targetIndex = index;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
          else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') targetIndex = 0;
          else if (event.key === 'End') targetIndex = tabs.length - 1;
          else return;
          event.preventDefault();
          section.classList.add('is-keyboard-nav');
          activate(tabs[targetIndex], true);
          window.requestAnimationFrame(() => window.requestAnimationFrame(() => section.classList.remove('is-keyboard-nav')));
        }, { signal: controller.signal });
      });

      const renderScroll = () => {
        queued = false;
        if (reducedMotion || !tabs.length || !scene) return;
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        const sectionRect = section.getBoundingClientRect();
        let nextIndex = 0;

        if (desktop) {
          const stickyTop = Number.parseFloat(window.getComputedStyle(scene).top) || 0;
          const travel = Math.max(1, section.offsetHeight - scene.offsetHeight);
          const progress = Math.min(1, Math.max(0, (stickyTop - sectionRect.top) / travel));
          nextIndex = Math.min(tabs.length - 1, Math.floor(progress * tabs.length));
          if (background) background.style.transform = '';
          applyVisualMotion(progress, true);
          if (aura) {
            aura.style.opacity = (0.2 + progress * 0.3).toFixed(3);
            aura.style.transform = `translate3d(${(-4 + progress * 6).toFixed(2)}%,${((progress - 0.5) * 3).toFixed(2)}%,0) rotate(${(-5 + progress * 4).toFixed(2)}deg) scaleX(${(0.86 + progress * 0.16).toFixed(3)})`;
          }
        } else {
          if (background) background.style.transform = '';
          const mobileTravel = Math.max(1, sectionRect.height + window.innerHeight * 0.35);
          const mobileProgress = Math.min(1, Math.max(0, (window.innerHeight * 0.85 - sectionRect.top) / mobileTravel));
          applyVisualMotion(mobileProgress, false);
          if (aura) {
            aura.style.opacity = '';
            aura.style.transform = '';
          }
          if (sectionRect.top < window.innerHeight && sectionRect.bottom > 0) {
            const anchor = window.innerHeight * 0.55;
            let nearest = Number.POSITIVE_INFINITY;
            tabs.forEach((tab, index) => {
              const rect = tab.getBoundingClientRect();
              const distance = Math.abs(rect.top + rect.height / 2 - anchor);
              if (distance < nearest) {
                nearest = distance;
                nextIndex = index;
              }
            });
          } else if (sectionRect.bottom <= 0) nextIndex = tabs.length - 1;
        }

        if (nextIndex !== activeIndex) activate(tabs[nextIndex]);
      };

      const queueRender = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(renderScroll);
      };

      const initial = tabs.find((tab) => tab.getAttribute('aria-expanded') === 'true') || tabs[0];
      if (initial) activate(initial);
      section.dataset.motion = reducedMotion ? 'reduced' : 'scroll';
      section.dataset.motionProgress = '0.0000';
      if (!reducedMotion) {
        renderScroll();
        window.addEventListener('scroll', queueRender, { passive: true, signal: controller.signal });
        window.addEventListener('resize', queueRender, { passive: true, signal: controller.signal });
      } else {
        if (visual) visual.style.transform = '';
        if (beam) {
          beam.style.opacity = '';
          beam.style.transform = '';
        }
        if (lens) {
          lens.style.opacity = '';
          lens.style.transform = '';
        }
      }
    });
  }

  const galixPackText = (() => {
    const table = {
      es: { gift: (n) => `Regalo ${n}`, title: (p) => `Proyector · ${p}`, label: (p) => `Proyector + ${p}`, gifts: (n) => `+ ${n} regalos incluidos`, contentsPack: (p) => `Contenido del pack ${p}`, contents: (p) => `Contenido de ${p}`, selected: (p) => `${p} seleccionado.` },
      en: { gift: (n) => `Gift ${n}`, title: (p) => `Projector · ${p}`, label: (p) => `Projector + ${p}`, gifts: (n) => `+ ${n} gifts included`, contentsPack: (p) => `What's in the ${p} pack`, contents: (p) => `What's in ${p}`, selected: (p) => `${p} selected.` },
      de: { gift: (n) => `Geschenk ${n}`, title: (p) => `Projektor · ${p}`, label: (p) => `Projektor + ${p}`, gifts: (n) => `+ ${n} Geschenke inklusive`, contentsPack: (p) => `Inhalt des Sets ${p}`, contents: (p) => `Inhalt: ${p}`, selected: (p) => `${p} ausgewählt.` }
    };
    return table[(document.documentElement.lang || 'es').slice(0, 2).toLowerCase()] || table.es;
  })();

  function initPacks(scope = document) {
    scope.querySelectorAll('[data-packs]:not([data-galix-ready])').forEach((section) => {
      section.dataset.galixReady = 'true';
      const controller = new AbortController();
      section.galixController = controller;
      const options = [...section.querySelectorAll('[data-pack-option]')];
      const price = section.querySelector('[data-pack-price]');
      const compare = section.querySelector('[data-pack-compare]');
      const saving = section.querySelector('[data-pack-saving]');
      const details = section.querySelector('[data-pack-details]');
      const current = section.querySelector('[data-pack-current]');
      const packImage = section.querySelector('[data-pack-image]');
      const imageFrame = section.querySelector('[data-pack-image-frame]');
      const packContents = section.querySelector('[data-pack-contents]');
      const packThumbImage = section.querySelector('[data-pack-thumb-image]');
      const packThumbLabel = section.querySelector('[data-pack-thumb-label]');
      const gifts = [...section.querySelectorAll('[data-pack-gift]')];
      const forms = [...section.querySelectorAll('[data-pack-form]')];
      const switcher = section.querySelector('.galix-pack-switch');
      const comparisonOptions = [...section.querySelectorAll('[data-pack-comparison-option]')];
      const comparisonTitle = section.querySelector('[data-pack-comparison-title]');
      const comparisonPackLabel = section.querySelector('[data-pack-comparison-pack-label]');
      const comparisonPrice = section.querySelector('[data-pack-comparison-price]');
      const comparisonCompare = section.querySelector('[data-pack-comparison-compare]');
      const comparisonGiftNote = section.querySelector('[data-pack-comparison-gift-note]');
      const comparisonContents = section.querySelector('.galix-pack-comparison__contents');
      const comparisonImage = section.querySelector('[data-pack-comparison-image]');
      const comparisonGifts = [...section.querySelectorAll('[data-pack-comparison-gift]')];
      const motionTargets = [imageFrame, details].filter(Boolean);
      let transitionToken = 0;
      let swapTimer;

      const cancelPackMotion = () => {
        window.clearTimeout(swapTimer);
        motionTargets.forEach((target) => target.getAnimations().forEach((animation) => animation.cancel()));
      };

      const centerOption = (option, behavior = 'smooth') => {
        if (!switcher || switcher.scrollWidth <= switcher.clientWidth) return;
        const optionRect = option.getBoundingClientRect();
        const switchRect = switcher.getBoundingClientRect();
        switcher.scrollTo({
          left: switcher.scrollLeft + optionRect.left - switchRect.left - (switcher.clientWidth - optionRect.width) / 2,
          behavior
        });
      };

      const applyPackContent = (option) => {
        if (price) price.textContent = option.dataset.price;
        if (compare) compare.textContent = option.dataset.compare;
        if (saving) saving.textContent = option.dataset.saving;
        const giftCount = Math.min(gifts.length, Math.max(0, Number(option.dataset.giftCount) || 0));
        gifts.forEach((gift, index) => {
          gift.hidden = index >= giftCount;
          gift.style.setProperty('--gift-index', index + 1);
          const label = gift.querySelector('[data-pack-gift-label]');
          if (label) label.textContent = galixPackText.gift(index + 1);
        });
        const nextImage = option.dataset.image;
        const nextAlt = option.dataset.imageAlt || option.textContent.trim();
        const packName = option.querySelector('.galix-pack-switch__name strong')?.textContent.trim() || '';
        if (comparisonTitle) comparisonTitle.textContent = galixPackText.title(packName);
        if (comparisonPackLabel) comparisonPackLabel.textContent = galixPackText.label(packName);
        if (comparisonPrice) comparisonPrice.textContent = option.dataset.price || '';
        if (comparisonCompare) {
          comparisonCompare.textContent = option.dataset.compare || '';
          comparisonCompare.hidden = option.dataset.hasCompare !== 'true';
        }
        if (comparisonGiftNote) {
          const giftLabelCount = Math.max(0, Number(option.dataset.giftCount) || 0);
          comparisonGiftNote.textContent = giftLabelCount ? galixPackText.gifts(giftLabelCount) : '';
          comparisonGiftNote.hidden = giftCount === 0;
        }
        if (comparisonContents) {
          comparisonContents.setAttribute('aria-label', galixPackText.contentsPack(packName));
          comparisonContents.style.setProperty('--pack-comparison-item-count', giftCount + 1);
        }
        if (comparisonImage && nextImage && comparisonImage.src !== nextImage) comparisonImage.src = nextImage;
        if (comparisonImage) comparisonImage.alt = nextAlt;
        comparisonGifts.forEach((gift) => {
          gift.hidden = Number(gift.dataset.packComparisonGift) > giftCount;
        });
        if (packImage && nextImage && packImage.src !== nextImage) {
          packImage.src = nextImage;
          packImage.alt = nextAlt;
        }
        if (packThumbImage && nextImage) {
          packThumbImage.src = nextImage;
          packThumbImage.alt = nextAlt;
        }
        if (packThumbLabel) packThumbLabel.textContent = option.dataset.imageAlt || option.textContent.trim();
        if (packContents) packContents.setAttribute('aria-label', galixPackText.contents(option.textContent.trim()));
        if (current) current.textContent = `${galixPackText.selected(option.textContent.trim())} ${option.dataset.price}. ${option.dataset.saving}.`;
      };

      const transitionPackContent = (option, immediate = false) => {
        const token = ++transitionToken;
        cancelPackMotion();
        const mobile = window.matchMedia('(max-width: 600px)').matches;
        if (immediate || reducedMotion || !motionTargets.length) {
          applyPackContent(option);
          return;
        }
        motionTargets.forEach((target) => target.animate(
          [{ opacity: Number.parseFloat(getComputedStyle(target).opacity) || 1 }, { opacity: mobile ? 0.9 : 0.8 }],
          { duration: mobile ? 45 : 90, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' }
        ));
        swapTimer = window.setTimeout(() => {
          if (token !== transitionToken) return;
          applyPackContent(option);
          motionTargets.forEach((target) => {
            target.getAnimations().forEach((animation) => animation.cancel());
            target.animate(
              [{ opacity: mobile ? 0.9 : 0.8, transform: 'translateY(2px)' }, { opacity: 1, transform: 'translateY(0)' }],
              { duration: mobile ? 170 : 280, easing: 'cubic-bezier(.4,0,.2,1)' }
            );
          });
        }, mobile ? 45 : 90);
      };

      const activate = (option, focus = false, immediate = false) => {
        if (!immediate && section.dataset.selectedVariant === option.dataset.variantId) {
          if (focus) option.focus();
          return;
        }
        options.forEach((item) => {
          const active = item === option;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-checked', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        comparisonOptions.forEach((item) => {
          const active = options[Number(item.dataset.packComparisonOption)] === option;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-pressed', String(active));
        });
        forms.forEach((form) => { form.hidden = form.dataset.packForm !== option.dataset.variantId; });
        section.dataset.selectedVariant = option.dataset.variantId;
        transitionPackContent(option, immediate);
        centerOption(option, reducedMotion || immediate ? 'auto' : 'smooth');
        if (focus) {
          option.focus();
        }
      };
      options.forEach((option, index) => {
        option.addEventListener('click', () => activate(option), { signal: controller.signal });
        option.addEventListener('keydown', (event) => {
          let targetIndex = index;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % options.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index - 1 + options.length) % options.length;
          else if (event.key === 'Home') targetIndex = 0;
          else if (event.key === 'End') targetIndex = options.length - 1;
          else return;
          event.preventDefault();
          activate(options[targetIndex], true);
        }, { signal: controller.signal });
      });
      comparisonOptions.forEach((comparisonOption) => {
        comparisonOption.addEventListener('click', () => {
          const option = options[Number(comparisonOption.dataset.packComparisonOption)];
          if (option) activate(option);
        }, { signal: controller.signal });
      });
      const initial = options.find((option) => option.getAttribute('aria-checked') === 'true') || options[0];
      if (initial) {
        activate(initial, false, true);
        requestAnimationFrame(() => centerOption(initial, 'auto'));
      }
      controller.signal.addEventListener('abort', cancelPackMotion, { once: true });
    });
  }

  function initWorlds(scope = document) {
    scope.querySelectorAll('[data-worlds]:not([data-worlds-expand]):not([data-galix-ready])').forEach((section) => {
      section.dataset.galixReady = 'true';
      const worlds = [...section.querySelectorAll('[data-world]')];
      const title = section.querySelector('[data-world-title]');
      const description = section.querySelector('[data-world-description]');
      const thumb = section.querySelector('[data-world-thumb]');
      worlds.forEach((world) => world.addEventListener('click', () => {
        worlds.forEach((item) => {
          const active = item === world;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        if (title) title.textContent = world.dataset.title;
        if (description) description.textContent = world.dataset.description;
        const image = world.querySelector('img');
        if (thumb && image) thumb.src = image.src;
      }));
    });
  }

  function initFaq(scope = document) {
    scope.querySelectorAll('[data-faq]:not([data-galix-ready])').forEach((section) => {
      section.dataset.galixReady = 'true';
      const buttons = [...section.querySelectorAll('[data-faq-category]')];
      const items = [...section.querySelectorAll('[data-faq-item]')];
      const activate = (button) => {
        const category = button.dataset.faqCategory;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        items.forEach((item) => {
          item.hidden = item.dataset.category !== category;
          if (item.hidden) item.open = false;
        });
        const first = items.find((item) => !item.hidden);
        if (first) first.open = true;
      };
      buttons.forEach((button) => button.addEventListener('click', () => activate(button)));
      const active = buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
      if (active) activate(active);
    });
  }

  function init(scope = document) {
    initReveals(scope);
    initHeader(scope);
    initComparison(scope);
    initBenefits(scope);
    initPacks(scope);
    initWorlds(scope);
    initFaq(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-galix-header]')?.galixController?.abort();
    event.target.querySelector('[data-comparison]')?.galixController?.abort();
    event.target.querySelector('[data-benefits]')?.galixController?.abort();
    const packs = event.target.matches?.('[data-packs]') ? event.target : event.target.querySelector('[data-packs]');
    packs?.galixController?.abort();
  });
})();
